import {isString, isNumber} from 'lodash';

/**
 * Clones an entire object by value.
 */
export function deepClone<T>(obj: T): T {
  const json = JSON.stringify(obj);

  // JSON.stringify will return `undefined` when the `obj` is either
  // `undefined` or a function. We can't clone those values to begin with,
  // so it's safe to return the original. This satisfies the generic type `T`
  if (json === undefined) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        '`deepClone` was given an uncloneable value, such as a `undefined` or a function. ' +
          'Please fix the underlying cause.'
      );
    }

    return obj;
  }

  const clone = JSON.parse(json);
  return clone;
}

/**
 * Detects whether a object is not undefined AND not null.
 */
export function hasValue(obj: unknown): boolean {
  return obj !== null && obj !== undefined;
}

/**
 * Detects whether a string/object/number value is numeric.
 */
export function isNumeric(value: unknown): value is string {
  if (value === null || value === undefined) {
    return false;
  }

  if (isNumber(value)) {
    return true;
  }

  if (isString(value)) {
    const cleanStr = value.trim();

    // NOTE: we're passing what is _definitely_ a string to `isNaN`, which only
    // accepts numbers.
    const isNotNum = isNaN(cleanStr as any);

    if (cleanStr === '') {
      return false;
    }

    return !isNotNum;
  }

  return false;
}

/**
 * If a value looks numeric, converts to a proper number if possible
 */
export function toNumber<T>(value: T): T | number {
  if (isNumber(value)) {
    return value;
  }

  if (isString(value) && isNumeric(value)) {
    if (value.indexOf('.')) {
      return parseFloat(value);
    } else {
      return parseInt(value, 10);
    }
  }

  return value;
}
